// File → ImageSource adapter. The image-editor overlay accepts
// `ImageSource[]` (blob + intrinsic dimensions + id). When the
// user pastes an image into the composer, the editor hands us a
// `File` (clipboard payload). We resolve the file's intrinsic
// dimensions by loading it into an `<img>` and read `naturalWidth/
// Height`; the resulting `ImageSource` drops into the same overlay
// pipeline as picked-from-disk images.
//
// Web-only: native code paths receive a `nativePath` property on
// the File-shaped object and would resolve dimensions natively.

import type { ImageSource, ImageId } from '../../imageeditor/types';

function generateImageId(): ImageId {
  // Lightweight unique id — collision odds are negligible inside a
  // single composer session.
  const ts = Date.now().toString(36);
  const rnd = Math.random().toString(36).slice(2, 8);
  return `paste-${ts}-${rnd}` as ImageId;
}

/**
 * Resolve a clipboard / drop `File` (or File-like Blob with a `name`)
 * into the `ImageSource` shape the image-editor overlay consumes.
 *
 * Dimension resolution strategy, in order:
 *   1. `createImageBitmap(blob)` — decodes the raw bytes inside the
 *      browser's image pipeline. Does NOT go through `img-src` CSP,
 *      so it works even when the host's CSP blocks `blob:`.
 *   2. `<img src="blob:..."/>` fallback — for browsers without
 *      `createImageBitmap` (none in our supported matrix today,
 *      but kept defensively).
 */
export async function fileToImageSource(file: File): Promise<ImageSource> {
  if (!file.type.startsWith('image/')) {
    throw new Error(`fileToImageSource: not an image (${file.type})`);
  }
  const dims = await resolveDimensions(file);
  return {
    id: generateImageId(),
    blob: file,
    widthPx: dims.width,
    heightPx: dims.height,
  };
}

interface Dims {
  readonly width: number;
  readonly height: number;
}

async function resolveDimensions(file: File): Promise<Dims> {
  // Preferred path: createImageBitmap decodes the blob's raw bytes
  // through the browser's image codec without ever hitting the
  // CSP fetch / img-src layer.
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      const dims: Dims = { width: bitmap.width, height: bitmap.height };
      // Release the bitmap eagerly — the editor doesn't need it.
      bitmap.close?.();
      return dims;
    } catch {
      // Fall through to the <img> path. Some encoders produce
      // images createImageBitmap rejects (e.g. malformed SVG)
      // but the legacy `<img>` decoder still accepts.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await loadImage(url);
    return { width: img.naturalWidth, height: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new Error('fileToImageSource: image decode failed'));
    img.src = url;
  });
}
